import { useMemo } from 'react';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import type { ActivityController } from '../controllers/ActivityController';
import { getActivityCommentCommands } from '@ayocore/activity/client/extensions';
import { useControllerSelector } from './useControllerSelector';

type UseActivityThreadsViewModelParams = {
  controller: ActivityController;
  currentUserId?: number | null;
};

function extractEventId(itemId: string | null | undefined): number | null {
  if (!itemId || !itemId.startsWith('event-')) return null;
  const raw = itemId.slice('event-'.length);
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Производный view-model для Activity:
 * сортировка тредов, обогащение chat-actions и флаги пагинации чата.
 */
export function useActivityThreadsViewModel({
  controller,
  currentUserId,
}: UseActivityThreadsViewModelParams) {
  const chatState = useControllerSelector(controller.chat, state => state);
  const rootState = useControllerSelector(controller, state => state);

  const selectedThread = rootState.selectedThread;
  const commentCommands = useMemo(() => getActivityCommentCommands(), []);

  const displayChatItems = useMemo(
    () =>
      chatState.chatItems.map(item => {
        if (item.kind !== 'comment') return item;

        const isOwn = Boolean(currentUserId && item.author_id === currentUserId);
        const actions: NonNullable<ActivityChatItem['actions']> = [];

        if (!item.deleted) {
          actions.push(
            { id: 'reply_comment', label: 'Ответить' },
            { id: 'pin_comment', label: 'Закрепить' }
          );
          if (isOwn) {
            actions.push(
              { id: 'edit_comment', label: 'Редактировать' },
              { id: 'delete_comment', label: 'Удалить', color: 'red' }
            );
          }

          const commandContext = {
            threadEntityType: selectedThread?.entity_type ?? null,
            threadEntityUuid: selectedThread?.entity_uuid ?? null,
            source: 'comment' as const,
            event: item,
            payload: null,
          };
          const availableCommands = commentCommands.filter(command =>
            command.isAvailable ? command.isAvailable(commandContext) : true
          );
          availableCommands.forEach(command => {
            actions.push({
              id: `run_command:${command.id}`,
              label: command.comment.label || command.label,
            });
          });
        }

        return {
          ...item,
          is_own: isOwn,
          actions: actions.length > 0 ? actions : undefined,
        };
      }),
    [
      chatState.chatItems,
      currentUserId,
      commentCommands,
      selectedThread?.entity_type,
      selectedThread?.entity_uuid,
    ]
  );

  const chatLoading = chatState.commentsLoading || chatState.eventsLoading;
  const chatErrorMessage = chatState.commentsErrorMessage || chatState.eventsErrorMessage;

  const lastChatEventId = useMemo(() => {
    if (chatState.chatItems.length === 0) return null;
    return extractEventId(chatState.chatItems[chatState.chatItems.length - 1]?.id ?? null);
  }, [chatState.chatItems]);

  const hasNewerMessages = useMemo(() => {
    if (!selectedThread?.last_event_id || !lastChatEventId) return false;
    return lastChatEventId < selectedThread.last_event_id;
  }, [selectedThread?.last_event_id, lastChatEventId]);

  const canAutoLoadOlder = chatState.commentsHasMoreOlder && !hasNewerMessages;

  return {
    displayChatItems,
    chatLoading,
    chatErrorMessage,
    hasNewerMessages,
    canAutoLoadOlder,
  };
}
